/**
 * Where this build of the app is mounted.
 *
 * ── Why this exists ───────────────────────────────────────────────────────
 *
 * The app was written to live at `/v2`, and 45 places said so in a string:
 * every route's `href`, the router's "is this URL mine?" test, `tripUrl`, the
 * shell's brand link, half a dozen screens' back-links. That was correct while
 * `/v2` was a folder sitting beside `app.html` on one hostname.
 *
 * It stopped being correct on 22 Aug 2026, when the app moved to its own
 * hostname — `app.mbmstudiotravel.com`, where it owns the whole path space and
 * the dashboard is `/dashboard`, not `/v2/dashboard`. The convention every
 * SaaS of any size follows: marketing on the naked domain, product on `app.`.
 *
 * ── One codebase, two mounts, on purpose ──────────────────────────────────
 *
 * `BASE` is read from Vite's `import.meta.env.BASE_URL`, which is whatever
 * `vite.config.js` was told. So the SAME source builds twice:
 *
 *   base '/'    → app.mbmstudiotravel.com/dashboard    (the new home)
 *   base '/v2/' → mbmstudiotravel.com/v2/dashboard     (kept alive for
 *                                                       existing bookmarks)
 *
 * That is the whole reason this is computed rather than hard-coded to ''. A
 * cutover that breaks every link an advisor saved is not a cutover, it is an
 * outage with a nicer name — so the old address keeps working and redirects
 * from Firebase Hosting, and if the new host has a bad day the old build is
 * still there and still correct.
 *
 * ── Nothing here is a decision ────────────────────────────────────────────
 *
 * Pure string work, no DOM, no `location`. `routes.js` decides what the
 * screens ARE; this only says what they are prefixed with.
 */

/**
 * The mount point, with no trailing slash: `''` at the domain root, `/v2` when
 * built into the old folder.
 *
 * Vite guarantees `BASE_URL` starts and ends with `/`, but a test importing
 * this outside a Vite transform gets `undefined` — hence the fallback.
 */
export const BASE = String(
  (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.BASE_URL) || '/'
).replace(/\/+$/, '')

/**
 * An in-app URL.
 *
 * Takes the path as the app thinks of it — `/dashboard`, `/itinerary?trip=1` —
 * and returns what the browser should be given. At the domain root that is the
 * same string back, which is the point: the call sites read as the screens
 * they name, and the prefix is somebody else's problem.
 *
 * @param {string} path - must start with `/`
 * @returns {string}
 */
export function appUrl(path) {
  const p = String(path || '')
  if (!p.startsWith('/')) return `${BASE}/${p}`
  /* `${BASE}${p}` and not a join: BASE is '' or '/v2', p always leads with a
     slash, so there is never a double slash to squash. */
  return `${BASE}${p}`
}

/**
 * Is this pathname one of ours?
 *
 * At `/v2` it asks whether the path is under that folder. At the domain root
 * EVERY path is ours — which is not a special case to code around, it is what
 * owning a hostname means, and the reason the app can finally have URLs that
 * read like the screens they show.
 *
 * @param {string} pathname
 * @returns {boolean}
 */
export function isAppPath(pathname) {
  const p = String(pathname || '')
  if (!BASE) return p.startsWith('/')
  return p === BASE || p.startsWith(`${BASE}/`)
}

/**
 * A pathname with the mount point taken off, so route matching never has to
 * know about it: `/v2/clients` and `/clients` both come back as `/clients`.
 *
 * @param {string} pathname
 * @returns {string}
 */
export function appPath(pathname) {
  const p = String(pathname || '')
  if (!BASE || !p.startsWith(BASE)) return p || '/'
  return p.slice(BASE.length) || '/'
}

/**
 * The marketing site, absolutely.
 *
 * ── Why this is not a relative path ───────────────────────────────────────
 *
 * `/privacy`, `/terms` and the original `/app` live on the OTHER hostname.
 * While the app sat at `mbmstudiotravel.com/v2` they were siblings and a bare
 * `/privacy` was right. On `app.mbmstudiotravel.com` a bare `/privacy`
 * resolves against the app's own host, where the catch-all rewrite hands back
 * `index.html` — so the advisor asks for the privacy policy and gets the
 * dashboard. Silent, and exactly the kind of thing a split like this breaks.
 *
 * Read from `VITE_SITE_ORIGIN` so staging points at staging. Empty by default,
 * which yields the old same-host behaviour — correct at the `/v2` mount and
 * correct in a test.
 *
 * @param {string} path - must start with `/`
 * @returns {string}
 */
export function siteUrl(path) {
  const origin = String(
    (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_SITE_ORIGIN) || ''
  ).replace(/\/+$/, '')
  return `${origin}${String(path || '')}`
}
